import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Shield, ShieldOff, Trash2, Mail, RefreshCw, X, 
  Clock, CheckCircle, XCircle, Copy, ExternalLink, Inbox, AlertTriangle,
  Activity, Loader2
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: '', name: '', role: 'admin', role_ids: [] });
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  // Contact-sync mailbox state
  const [mailboxes, setMailboxes] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [togglingUpn, setTogglingUpn] = useState(null);
  const [fullSyncLoading, setFullSyncLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressOperation, setProgressOperation] = useState(null); // 'cleanup' or 'sync'
  const [progressUpn, setProgressUpn] = useState(null);
  const [progressEvents, setProgressEvents] = useState([]);
  const [progressPolling, setProgressPolling] = useState(false);
  
  // Events tab state
  const [allEvents, setAllEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }

    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, invitationsRes, rolesRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/auth/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/auth/invitations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/roles`, { withCredentials: true })
      ]);

      const failures = [];

      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data);
      } else {
        failures.push(`users (${usersRes.reason?.response?.status || 'network'})`);
      }
      if (invitationsRes.status === 'fulfilled') {
        setInvitations(invitationsRes.value.data);
      } else {
        failures.push(`invitations (${invitationsRes.reason?.response?.status || 'network'})`);
      }
      if (rolesRes.status === 'fulfilled') {
        setRoles(rolesRes.value.data);
      } else {
        failures.push(`roles (${rolesRes.reason?.response?.status || 'network'})`);
      }

      if (failures.length > 0) {
        const has401 = [usersRes, invitationsRes, rolesRes].some(
          (r) => r.status === 'rejected' && r.reason?.response?.status === 401,
        );
        if (has401) {
          toast.error(
            `Session expired or missing permission for: ${failures.join(', ')}. Stay on this page — try refreshing or log out and back in manually.`,
            { duration: 8000 },
          );
        } else {
          toast.error(`Failed to load: ${failures.join(', ')}`);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Unexpected error loading user management');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/auth/users/${userId}/status`, {}, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/users/${userId}`, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteLoading(true);
    setInviteSuccess(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/invitations`, newInvite, {
        withCredentials: true
      });
      setInviteSuccess(response.data);
      setNewInvite({ email: '', name: '', role: 'admin', role_ids: [] });
      fetchData();
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const cancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/invitations/${invitationId}`, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const resendInvitation = async (invitationId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/invitations/resend/${invitationId}`, {}, {
        withCredentials: true
      });
      alert('Invitation resent successfully!');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    alert('Invitation link copied to clipboard!');
  };

  // Contact Sync mailboxes (Microsoft 365 tenant)
  const fetchMailboxes = async () => {
    setMailboxLoading(true);
    try {
      const [mbRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/contact-sync/mailboxes`),
        axios.get(`${BACKEND_URL}/api/contact-sync/status`),
      ]);
      setMailboxes(mbRes.data.mailboxes || []);
      setSyncStatus(statusRes.data);
    } catch (error) {
      console.error('Mailbox fetch error:', error);
      setSyncStatus({ graph_configured: false, graph_connected: false });
      toast.error(error.response?.data?.detail || 'Failed to load mailboxes');
    } finally {
      setMailboxLoading(false);
    }
  };

  const toggleMailboxSync = async (upn, enabled) => {
    setTogglingUpn(upn);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact-sync/mailboxes/toggle`, { upn, enabled });
      setMailboxes((prev) => prev.map((m) => (m.upn === upn ? { ...m, sync_enabled: enabled } : m)));
      
      if (response.data.full_sync_started) {
        toast.success(`Enabled sync for ${upn} - Full sync started! All JobTread contacts will be pushed.`, { duration: 5000 });
      } else {
        toast.success(`${enabled ? 'Enabled' : 'Disabled'} sync for ${upn}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mailbox');
    } finally {
      setTogglingUpn(null);
    }
  };

  // Fetch events for a specific UPN or all events
  const fetchEvents = useCallback(async (upn = null, limit = 50) => {
    try {
      const params = { limit };
      if (upn) params.upn = upn;
      const response = await axios.get(`${BACKEND_URL}/api/contact-sync/events`, { params });
      return response.data.events || [];
    } catch (error) {
      console.error('Failed to fetch events:', error);
      return [];
    }
  }, []);

  // Poll for progress updates
  const pollProgress = useCallback(async (upn, operation) => {
    const events = await fetchEvents(upn, 20);
    setProgressEvents(events);
    
    // Check if operation completed
    const completedEvent = events.find(e => 
      e.upn === upn && 
      (e.action === `${operation}_completed` || e.action === `${operation}_failed`)
    );
    
    if (completedEvent) {
      setProgressPolling(false);
      if (completedEvent.action.includes('completed')) {
        toast.success(`${operation === 'cleanup' ? 'Cleanup' : 'Sync'} completed for ${upn}!`);
      } else {
        toast.error(`${operation === 'cleanup' ? 'Cleanup' : 'Sync'} failed: ${completedEvent.error || 'Unknown error'}`);
      }
      return true; // Completed
    }
    return false; // Still running
  }, [fetchEvents]);

  // Start progress monitoring
  const startProgressMonitor = (upn, operation) => {
    setProgressUpn(upn);
    setProgressOperation(operation);
    setProgressEvents([]);
    setShowProgressModal(true);
    setProgressPolling(true);
  };

  // Effect to poll for progress
  useEffect(() => {
    if (!progressPolling || !progressUpn || !progressOperation) return;
    
    const interval = setInterval(async () => {
      const completed = await pollProgress(progressUpn, progressOperation);
      if (completed) {
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds
    
    // Initial poll
    pollProgress(progressUpn, progressOperation);
    
    return () => clearInterval(interval);
  }, [progressPolling, progressUpn, progressOperation, pollProgress]);

  // Load all events when Events tab is active
  useEffect(() => {
    if (activeTab === 'events') {
      setEventsLoading(true);
      fetchEvents(null, 100).then(events => {
        setAllEvents(events);
        setEventsLoading(false);
      });
    }
  }, [activeTab, fetchEvents]);

  const triggerFullSync = async (upn = null) => {
    if (!upn) {
      // Sync all - use original behavior
      setFullSyncLoading(true);
      try {
        await axios.post(`${BACKEND_URL}/api/contact-sync/full-sync-all`);
        toast.success(`Full sync started for all enabled mailboxes.`, { duration: 5000 });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start full sync');
      } finally {
        setFullSyncLoading(false);
      }
      return;
    }
    
    setFullSyncLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact-sync/full-sync`, { upn });
      startProgressMonitor(upn, 'full_sync');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start full sync');
    } finally {
      setFullSyncLoading(false);
    }
  };

  const triggerCleanup = async (upn) => {
    if (!window.confirm(`This will DELETE all JobTread contacts from ${upn}'s mailbox. Are you sure?`)) {
      return;
    }
    setCleanupLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact-sync/cleanup`, { upn });
      startProgressMonitor(upn, 'cleanup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'mailboxes' && mailboxes.length === 0 && !mailboxLoading) {
      fetchMailboxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');

  return (
    <AdminSidebar pageTitle="User Management" pageIcon={Users}>
      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('users')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'users'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Active Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'invitations'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="users-tab-invitations"
        >
          Pending Invitations ({pendingInvitations.length})
        </button>
        <button
          onClick={() => setActiveTab('mailboxes')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'mailboxes'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="users-tab-mailboxes"
        >
          Mailbox Sync
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'events'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="users-tab-events"
        >
          <span className="flex items-center gap-1.5">
            <Activity className="w-4 h-4" />
            Sync Events
          </span>
        </button>
      </div>

      {/* Header Actions */}
      <div className="flex justify-end mb-6">
        <Button onClick={() => setShowInviteModal(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Mail className="w-4 h-4 mr-2" /> Invite User
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : activeTab === 'mailboxes' ? (
        // Mailbox Sync Tab
        <div className="space-y-4" data-testid="mailbox-sync-section">
          {/* Connection status banner */}
          {!syncStatus ? null : !syncStatus.graph_configured ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="mailbox-status-not-configured">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Microsoft 365 not configured</p>
                <p className="mt-1">Set AZURE_TENANT_ID, AZURE_CLIENT_ID, AZURE_CLIENT_SECRET in backend .env.</p>
              </div>
            </div>
          ) : !syncStatus.graph_connected ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="mailbox-status-error">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-900">
                <p className="font-semibold">Couldn&apos;t reach Microsoft Graph</p>
                <p className="mt-1">{syncStatus.graph_message || 'Check Azure credentials and admin consent.'}</p>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="mailbox-status-connected">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-emerald-900">
                  <p className="font-semibold">Connected to Microsoft 365</p>
                  <p className="mt-1">
                    {syncStatus.tenant_users_visible} tenant users visible · {syncStatus.enabled_mailboxes} mailbox{syncStatus.enabled_mailboxes === 1 ? '' : 'es'} syncing · {syncStatus.total_synced_contacts} contacts mapped
                    {!syncStatus.jobtread_webhook_token_configured && ' · JobTread webhook token not set'}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchMailboxes} disabled={mailboxLoading} data-testid="mailbox-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${mailboxLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => triggerFullSync()} 
                disabled={fullSyncLoading || syncStatus?.enabled_mailboxes === 0}
                data-testid="mailbox-sync-all-btn"
                className="ml-2"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fullSyncLoading ? 'animate-spin' : ''}`} />
                Sync All Contacts
              </Button>
            </div>
          )}

          {/* Mailbox table */}
          {mailboxLoading && mailboxes.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <RefreshCw className="w-8 h-8 text-slate-300 mx-auto mb-4 animate-spin" />
              <p className="text-slate-500">Loading mailboxes from Microsoft 365…</p>
            </div>
          ) : mailboxes.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No mailboxes</h3>
              <p className="text-slate-600">No users were returned from your Microsoft 365 tenant.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b text-sm text-slate-600">
                Toggle which employee Outlook mailboxes receive synced JobTread customer + vendor contacts. JobTread is the source of truth — disabling a mailbox does not delete existing contacts.
              </div>
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Employee</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Last updated</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Contact Sync</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mailboxes.map((m) => (
                    <tr key={m.upn} className="hover:bg-slate-50" data-testid={`mailbox-row-${m.upn}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${m.sync_enabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                            <Inbox className={`w-5 h-5 ${m.sync_enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{m.display_name || '—'}</div>
                            {!m.account_enabled && (
                              <div className="text-xs text-amber-600">Account disabled in tenant</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 break-all">{m.mail || m.upn}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">{m.updated_at ? formatDate(m.updated_at) : '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {m.sync_enabled && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => triggerCleanup(m.upn)}
                                disabled={cleanupLoading || fullSyncLoading}
                                title="Delete all JobTread contacts from this mailbox"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => triggerFullSync(m.upn)}
                                disabled={fullSyncLoading || cleanupLoading}
                                title="Resync all contacts for this mailbox"
                                className="text-cyan-600 hover:text-cyan-700"
                              >
                                <RefreshCw className={`w-4 h-4 ${fullSyncLoading ? 'animate-spin' : ''}`} />
                              </Button>
                            </>
                          )}
                          <Switch
                            checked={!!m.sync_enabled}
                            disabled={togglingUpn === m.upn || !m.account_enabled}
                            onCheckedChange={(checked) => toggleMailboxSync(m.upn, checked)}
                            data-testid={`mailbox-toggle-${m.upn}`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'users' ? (
        // Users Tab
        users.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Admin Users</h3>
            <p className="text-slate-600">Invite your first admin user to get started.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Last Login</th>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                          <span className="text-cyan-600 font-medium">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{user.name}</div>
                          <div className="text-sm text-slate-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                        user.role === 'super_admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-slate-100 text-slate-800'
                      }`}>
                        <Shield className="w-3 h-3" />
                        {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">
                      {formatDate(user.last_login)}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          {user.user_id !== currentUser?.user_id && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleUserStatus(user.user_id)}
                                className={user.is_active ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}
                                title={user.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {user.is_active ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteUser(user.user_id)}
                                className="text-red-600 hover:text-red-700"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {user.user_id === currentUser?.user_id && (
                            <span className="text-sm text-slate-400 italic">You</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : activeTab === 'invitations' ? (
        // Invitations Tab
        pendingInvitations.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Pending Invitations</h3>
            <p className="text-slate-600">All invitations have been accepted or cancelled.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Invitee</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Invited By</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Expires</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingInvitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{invitation.name}</div>
                          <div className="text-sm text-slate-500">{invitation.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                        invitation.role === 'super_admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-slate-100 text-slate-800'
                      }`}>
                        <Shield className="w-3 h-3" />
                        {invitation.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">
                      {invitation.invited_by}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">
                      {formatDate(invitation.expires_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resendInvitation(invitation.id)}
                          className="text-cyan-600 hover:text-cyan-700"
                          title="Resend Invitation"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelInvitation(invitation.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Cancel Invitation"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : activeTab === 'events' ? (
        // Events Tab
        <div className="space-y-4" data-testid="events-section">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Sync Event History</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEventsLoading(true);
                fetchEvents(null, 100).then(events => {
                  setAllEvents(events);
                  setEventsLoading(false);
                });
              }}
              disabled={eventsLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${eventsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {eventsLoading ? (
            <div className="text-center py-12">Loading events...</div>
          ) : allEvents.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Events Yet</h3>
              <p className="text-slate-600">Sync events will appear here when contacts are synced.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Mailbox</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allEvents.map((event, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                        {formatDate(event.ts)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {event.upn || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          event.action?.includes('completed') ? 'bg-green-100 text-green-800' :
                          event.action?.includes('failed') ? 'bg-red-100 text-red-800' :
                          event.action?.includes('started') ? 'bg-blue-100 text-blue-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {event.action || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {event.ok ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {event.error ? (
                          <span className="text-red-600">{event.error}</span>
                        ) : event.debug_info ? (
                          <div className="space-y-0.5 text-xs">
                            {event.debug_info.account_name && (
                              <div><span className="font-medium">Account:</span> {event.debug_info.account_name}</div>
                            )}
                            {event.debug_info.total_synced !== undefined && (
                              <div><span className="font-medium">Synced:</span> {event.debug_info.total_synced}</div>
                            )}
                            {event.debug_info.total_merged !== undefined && (
                              <div><span className="font-medium">Merged:</span> {event.debug_info.total_merged}</div>
                            )}
                            {event.debug_info.contacts_with_email !== undefined && (
                              <div><span className="font-medium">With Email:</span> {event.debug_info.contacts_with_email}</div>
                            )}
                            {event.debug_info.contacts_without_email !== undefined && (
                              <div><span className="font-medium">Without Email:</span> {event.debug_info.contacts_without_email}</div>
                            )}
                            {event.debug_info.total_errors !== undefined && event.debug_info.total_errors > 0 && (
                              <div className="text-red-600"><span className="font-medium">Errors:</span> {event.debug_info.total_errors}</div>
                            )}
                            {event.debug_info.ms_contacts_deleted !== undefined && (
                              <div><span className="font-medium">Deleted:</span> {event.debug_info.ms_contacts_deleted}</div>
                            )}
                            {event.debug_info.sync_map_entries_deleted !== undefined && (
                              <div><span className="font-medium">Map Cleared:</span> {event.debug_info.sync_map_entries_deleted}</div>
                            )}
                            {event.debug_info.logo_uploaded !== undefined && (
                              <div>
                                <span className="font-medium">Logo:</span>{' '}
                                {event.debug_info.logo_uploaded ? (
                                  <span className="text-green-600">✓ Uploaded</span>
                                ) : (
                                  <span className="text-amber-600">✗ Not uploaded</span>
                                )}
                              </div>
                            )}
                            {event.debug_info.logo_url && (
                              <div className="truncate max-w-xs"><span className="font-medium">Logo URL:</span> {event.debug_info.logo_url}</div>
                            )}
                            {event.debug_info.website && (
                              <div><span className="font-medium">Website:</span> {event.debug_info.website}</div>
                            )}
                            {event.debug_info.custom_fields && event.debug_info.custom_fields.length > 0 && (
                              <div><span className="font-medium">Fields:</span> {event.debug_info.custom_fields.join(', ')}</div>
                            )}
                            {event.debug_info.archived_at && (
                              <div><span className="font-medium">Archived:</span> {event.debug_info.archived_at}</div>
                            )}
                            {event.debug_info.reason && (
                              <div><span className="font-medium">Reason:</span> {event.debug_info.reason}</div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {!isSuperAdmin && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> Only super admins can modify or remove admin users. 
            Contact your super admin if you need to make changes.
          </p>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Invite Admin User</h2>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteError('');
                    setInviteSuccess(null);
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {inviteSuccess ? (
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Invitation Sent!</h3>
                  <p className="text-slate-600 text-sm">
                    An invitation email has been sent to <strong>{inviteSuccess.email}</strong>
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-xs text-slate-500 mb-2">If the email doesn&apos;t arrive, share this link directly:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteSuccess.invitation_link}
                      className="flex-1 text-xs bg-white border rounded px-3 py-2 text-slate-600"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyInviteLink(inviteSuccess.invitation_link)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteSuccess(null);
                    }}
                    className="flex-1"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => setInviteSuccess(null)}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  >
                    Invite Another
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendInvite} className="p-6 space-y-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="user@example.com"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    An invitation email will be sent to this address
                  </p>
                </div>
                <div>
                  <Label htmlFor="name">Display Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={newInvite.name}
                    onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="John Doe"
                  />
                </div>
                {isSuperAdmin && (
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={newInvite.role_ids[0] || ''}
                      onValueChange={(value) => {
                        const selectedRole = roles.find(r => r.id === value);
                        setNewInvite({ 
                          ...newInvite, 
                          role: selectedRole?.slug || 'employee',
                          role_ids: [value]
                        });
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full bg-${role.color || 'slate'}-500`}></span>
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-500 mt-1">
                      Choose a role to assign to this user. Roles determine what the user can access.
                    </p>
                  </div>
                )}

                {inviteError && (
                  <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                    {inviteError}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteError('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={inviteLoading}
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                {progressPolling ? (
                  <Loader2 className="w-5 h-5 text-cyan-600 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                <h2 className="text-xl font-bold text-slate-900">
                  {progressOperation === 'cleanup' ? 'Cleaning Up Contacts' : 'Syncing Contacts'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowProgressModal(false);
                  setProgressPolling(false);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-slate-600 mb-4">
                {progressPolling ? (
                  <>Processing <strong>{progressUpn}</strong>... This may take a few minutes for large contact lists.</>
                ) : (
                  <>Operation completed for <strong>{progressUpn}</strong>.</>
                )}
              </p>
              
              {/* Recent Events */}
              <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Recent Activity</h4>
                {progressEvents.length === 0 ? (
                  <p className="text-sm text-slate-500">Waiting for events...</p>
                ) : (
                  <div className="space-y-2">
                    {progressEvents.slice(0, 10).map((event, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        {event.ok ? (
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${
                            event.action?.includes('completed') ? 'bg-green-100 text-green-800' :
                            event.action?.includes('failed') ? 'bg-red-100 text-red-800' :
                            event.action?.includes('started') ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {event.action}
                          </span>
                          {event.debug_info && (
                            <div className="text-slate-600 text-xs mt-1">
                              {event.debug_info.account_name && (
                                <div>📋 {event.debug_info.account_name}</div>
                              )}
                              {event.debug_info.total_synced !== undefined && (
                                <div>✓ {event.debug_info.total_synced} synced</div>
                              )}
                              {event.debug_info.total_merged !== undefined && event.debug_info.total_merged > 0 && (
                                <div>↔ {event.debug_info.total_merged} merged (same email)</div>
                              )}
                              {event.debug_info.contacts_with_email !== undefined && (
                                <div>📧 {event.debug_info.contacts_with_email} with email</div>
                              )}
                              {event.debug_info.contacts_without_email !== undefined && (
                                <div>⚠ {event.debug_info.contacts_without_email} without email</div>
                              )}
                              {event.debug_info.ms_contacts_deleted !== undefined && (
                                <div>🗑 {event.debug_info.ms_contacts_deleted} deleted</div>
                              )}
                              {event.debug_info.total_errors !== undefined && event.debug_info.total_errors > 0 && (
                                <div className="text-red-600">❌ {event.debug_info.total_errors} errors</div>
                              )}
                              {event.debug_info.logo_uploaded !== undefined && (
                                <div>
                                  {event.debug_info.logo_uploaded ? '🖼 Logo uploaded' : '⚠ Logo not uploaded'}
                                </div>
                              )}
                              {event.debug_info.logo_url && (
                                <div className="truncate">🔗 {event.debug_info.logo_url.substring(0, 50)}...</div>
                              )}
                              {event.debug_info.website && (
                                <div>🌐 {event.debug_info.website}</div>
                              )}
                            </div>
                          )}
                          {event.error && (
                            <span className="text-red-600 text-xs block truncate">{event.error}</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(event.ts).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {!progressPolling && (
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => {
                      setShowProgressModal(false);
                      setProgressPolling(false);
                    }}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminSidebar>
  );
};

export default AdminUsersPage;
